import React, { useId, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import ImportEntryRow from '../../componentsV2/Import/ImportEntryRow';
import DisplayErroredImport from '../../componentsV2/Import/DisplayErroredImport';
import { formatImportDateTime } from '../../utils/dates';
import {
  parseLegacyImportCounts,
  formatLegacyImportStatus,
  useLegacyImportEnhancements,
} from '../../utils/legacyImports';
import './legacyImportHtml.scss';

const ImportPage = ({
  importedFile, entries, totalEntries, hrefLink, handleDownloadFile,
}) => {
  const pageId = useId();
  const legacyHtmlRef = useRef(null);

  const statusTextColor = importedFile?.status === 'error' ? 'text-vamos-red' : 'text-vamos-green4';

  const importEntries = totalEntries;
  const importErrors = importedFile?.raw_data?.entry_errors;

  const isLegacyImport = !importedFile?.raw_data && !!importedFile?.html;
  const formattedImportDate = importedFile ? formatImportDateTime(importedFile.created_at) : '';

  const legacyCounts = useMemo(
    () => (isLegacyImport ? parseLegacyImportCounts(importedFile?.html) : null),
    [isLegacyImport, importedFile?.html],
  );

  useLegacyImportEnhancements({
    ref: legacyHtmlRef,
    importType: importedFile?.import_type,
    isLegacyImport,
    html: importedFile?.html,
  });

  if (!importedFile) return null;

  // type of import comes in singular form
  const typeOfData = importEntries <= 1 ? importedFile.import_type : `${importedFile.import_type}s`;

  // display the import status message if any successful entries
  const importStatus = importEntries - importErrors === importEntries
    ? `${importEntries} of ${importEntries} ${typeOfData} imported successfully on ${formattedImportDate}.`
    : `${importErrors} of ${importEntries} ${typeOfData} failed validation on ${formattedImportDate}.`;

  // display the error message if no entries
  const errorMessage = (importedFile.raw_data?.errors ?? []).reduce((acc, error) => {
    // split error messages by newline character comes from the backend
    if (error.includes('\\n')) {
      // split is necessary to avoid nested arrays
      acc.push(...error.split('\\n'));
    } else {
      acc.push(error);
    }
    return acc;
  }, []);

  let statusContent;
  if (importEntries > 0) {
    statusContent = importStatus;
  } else if (isLegacyImport) {
    statusContent = formatLegacyImportStatus({
      counts: legacyCounts,
      importType: importedFile.import_type,
      formattedDate: formattedImportDate,
    }) || `Imported on ${formattedImportDate}.`;
  } else {
    statusContent = <DisplayErroredImport errors={errorMessage} />;
  }

  const hasInlineLayout = importEntries > 0 || isLegacyImport;

  return (
    <div className="lg:w-2/3 mt-1">
      <div className={`flex ${hasInlineLayout ? 'justify-between' : 'flex-col gap-3 ml-12'} text-lg`}>
        <div className={`${statusTextColor} ${!hasInlineLayout ? 'pl-4' : ''}`}>
          {statusContent}
        </div>
        {hrefLink ? (
          <div>
            <button
              type="button"
              className="text-lg text-vamos-black underline"
              onClick={() => handleDownloadFile()}
              data-testid="download-imported-file"
            >
              Download this file
            </button>
          </div>
        )
          : <div data-testid="download-not-available">File not available for download.</div>}
      </div>
      <div className="flex flex-col mt-5 gap-6">
        {importEntries > 0
          && entries.map((entry, index) => (
            // index fallback is safe — import results are static and never reordered
            <ImportEntryRow entry={entry} key={entry.id || `${pageId}_${index}`} type={importedFile.import_type} />
          ))}
        {isLegacyImport && (
          <div
            ref={legacyHtmlRef}
            data-testid="legacy-import-html"
            data-import-type={importedFile.import_type}
            className="legacy-import-html"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(importedFile.html) }}
          />
        )}
      </div>
    </div>
  );
};

ImportPage.propTypes = {
  importedFile: PropTypes.object.isRequired,
  entries: PropTypes.array.isRequired,
  totalEntries: PropTypes.number.isRequired,
  hrefLink: PropTypes.string.isRequired,
  handleDownloadFile: PropTypes.func.isRequired,
};

export default ImportPage;
